<template>
  <el-form
    ref="custom-data"
    :model="attributes"
    class="profile-tab"
    :key="key"
    :style="{ height: height, overflow: 'scroll', 'padding-bottom': '50px' }"
  >
    <form-item
      class="flexbox"
      v-for="(val, index) in attributes.data"
      :label="val[0]"
      :key="val[0]"
      :prop="'data.' + index + '.1'"
      :rules="rules[val[0]] || null"
    >
      <el-tooltip
        :content="getUnmetTooltipMessage(val[0])"
        placement="top"
        effect="light"
        :disabled="!hasUnmetAttributeConditions(val[0])"
      >
        <el-date-picker
          :class="['borderless-datepicker', getUnmetConditionsClass(val[0])]"
          v-if="attrTypes[val[0]] && attrTypes[val[0]] === 'datetime'"
          v-model="customAtts[val[0]]"
          type="datetime"
          format="yyyy-MM-dd HH:mm:ss"
          value-format="yyyy-MM-dd HH:mm:ss"
          @input="onChange"
          :clearable="false"
        />
        <el-date-picker
          :class="['borderless-datepicker', getUnmetConditionsClass(val[0])]"
          v-else-if="attrTypes[val[0]] && ['date'].includes(attrTypes[val[0]])"
          v-model="customAtts[val[0]]"
          format="yyyy-MM-dd"
          value-format="yyyy-MM-dd"
          @input="onChange"
          :clearable="false"
        />

        <el-input
          v-else
          :class="['borderless', getUnmetConditionsClass(val[0])]"
          v-model="customAtts[val[0]]"
          @input="onChange(val[0], rules[val[0]] || null)"
        />
      </el-tooltip>
    </form-item>
  </el-form>
</template>

<script>
import { EventBus } from '@/core/EventBus'
import _ from 'lodash'
import FormItem from '@/common/components/form/FormItem.vue'
import RestApi from '@/core/services/RestApi'
import { projectEndpoint } from '@/core/helpers'

export default {
  name: 'PreviewContentCustomDataTab',
  components: { FormItem },
  data() {
    return {
      height: '450px',
      attributes: {},
      key: null,
      rules: {},
      timeoutId: null,
      customAtts: {},
      data: [],
      loading: false,
      attrTypes: {},
      unmetConditions: []
    }
  },
  mounted() {
    this.calculateHeight()
    EventBus.$on('set-profile-attributes', attributes => {
      this.customAtts = _.cloneDeep(attributes)
      this.setAttributeRules()
      this.attributes = { data: Object.entries(this.customAtts) }
    })
    EventBus.$on('set-unmet-attribute-conditions', unmetConditions => {
      this.unmetConditions = unmetConditions
    })
  },
  methods: {
    setAttributeRules() {
      RestApi(
        projectEndpoint({
          method: 'get',
          url: `attributes`,
          params: {
            titles: Object.keys(this.customAtts),
            attribute_profile: 1
          }
        })
      ).then(({ data }) => {
        this.rules = {}
        var validator = (rule, value, callback) => {
          value = this.customAtts[rule.name]
          if (value === null) {
            callback()
            return
          }
          let valid = true
          switch (rule.type) {
            case 'boolean':
              if (!['1', '0', 1, 0].includes(value)) {
                valid = false
              }
              break
            case 'number':
              if (isNaN(parseInt(value)) || isNaN(value)) {
                valid = false
              }
              break
            case 'integer': {
              if (
                isNaN(parseInt(value)) ||
                isNaN(value) ||
                !_.isInteger(Number(value)) ||
                value.toString().includes('.')
              ) {
                valid = false
              }
              break
            }
          }

          if (!valid && value.toString().length !== 0) {
            callback(new Error(rule.name + ' is not a ' + rule.type))
          } else {
            callback()
          }
        }
        data.forEach(attr => {
          this.attrTypes[attr.name] = attr.type
          let type = null
          if (['number', 'unenforced_number'].includes(attr.type)) {
            type = 'number'
          }
          if (['integer', 'boolean'].includes(attr.type)) {
            type = attr.type
          }
          if (type) {
            this.rules[attr.name] = {
              name: attr.name,
              type: type,
              validator: validator,
              trigger: ['blur', 'change']
            }
          }
        })

        this.key = new Date().getTime()
      })
    },
    onChange(attrName, rules = null) {
      clearTimeout(this.timeoutId)

      this.timeoutId = setTimeout(() => {
        this.parseBoolAttribute(attrName, rules)
        this.$refs['custom-data'].validate().then(() => {
          this.$emit('data-change', _.cloneDeep(this.customAtts))
        })
      }, 400)
    },
    parseBoolAttribute(attrName, rules) {
      if (rules && rules.type === 'boolean') {
        if (
          ['0', 'no', 'n', 'false', 'f'].includes(this.customAtts[attrName])
        ) {
          this.customAtts[attrName] = 0
        }
        if (
          ['1', 'yes', 'y', 'true', 't'].includes(this.customAtts[attrName])
        ) {
          this.customAtts[attrName] = 1
        }
      }
    },
    calculateHeight() {
      // Calculate dynamic height for attributes section
      let height = 0
      let minHeight = 250
      if (!document.querySelector('.sidebar-ct')) {
        this.height = '450px'
      }

      let sidebarHeight = document.querySelector('.sidebar-ct').clientHeight
      let optionsHeight = 0
      document.querySelectorAll('.campaign-preview-option').forEach(el => {
        optionsHeight += el.clientHeight || 0
      })
      height = sidebarHeight - optionsHeight - 165

      this.height = Math.max(height, minHeight) + 'px'
    },
    isUnmetAttributeConditionWithFallback(attribute) {
      return !!this.unmetConditions['unmetConditionAttributes'].includes(
        attribute
      )
    },
    isUnmetAttributeConditionWithNoFallback(attribute) {
      const key = 'unmetConditionAttributesNoFallback'
      const unmetConditions = this.unmetConditions[key]

      return !!unmetConditions.includes(attribute)
    },
    hasUnmetAttributeConditions(attribute) {
      return (
        this.isUnmetAttributeConditionWithFallback(attribute) ||
        this.isUnmetAttributeConditionWithNoFallback(attribute)
      )
    },
    getUnmetConditionsClass(attribute) {
      return { 'unmet-conditions': this.hasUnmetAttributeConditions(attribute) }
    },
    getUnmetTooltipMessage(attribute) {
      if (this.isUnmetAttributeConditionWithFallback(attribute)) {
        return this.$t(
          'CampaignForm_Preview_Attribute_Condition_Fallback_Applied'
        )
      }
      if (this.isUnmetAttributeConditionWithNoFallback(attribute)) {
        return this.$t(
          'CampaignForm_Preview_Attribute_Condition_No_Fallback_Available'
        )
      }
    }
  },
  destroyed() {
    EventBus.$off('set-profile-attributes')
  }
}
</script>

<style scoped lang="scss">
.flexbox {
  display: flex;
  justify-content: space-between;
}

.flexbox:before,
.flexbox::after {
  display: inherit;
  content: none;
}

/deep/ .flexbox .el-form-item__label {
  font-size: 12px;
  font-weight: 600;
  padding-top: 4px;
}

/deep/ .borderless > .el-input__inner {
  border: 0;
  border-bottom: 1px solid #c2c7d3;
  border-radius: 0;
}

/deep/ .borderless-datepicker {
  .el-input__inner {
    border: 0;
    border-bottom: 1px solid #c2c7d3;
    border-radius: 0;
    padding: 0 15px;
  }

  .el-input__prefix {
    right: 5px;

    .el-icon-date,
    .el-icon-time {
      float: right;
    }
  }
}

/deep/ .unmet-conditions > .el-input__inner {
  background-color: #fde8e9;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/preview/content-data-tabs/PreviewContentCustomDataTab.vue